import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ImagePlus, X } from "lucide-react";
import { fileToDataUrl } from "@/lib/image";
import { DTYPES } from "@/lib/coupon";
import { toast } from "sonner";

// Reusable coupon configuration fields (type, value, min spend, description, images, expiry).
// `value` is the coupon object, `onChange(patch)` merges a partial update.
export default function CouponConfigForm({ value, onChange, idPrefix = "coupon" }) {
  const draft = value || {};
  const valDisabled = draft.discount_type === "bogo";

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    try {
      const cur = draft.images || [];
      const slots = 2 - cur.length;
      if (slots <= 0) return toast.error("Massimo 2 immagini");
      const urls = await Promise.all(files.slice(0, slots).map((f) => fileToDataUrl(f, 1000)));
      onChange({ images: [...cur, ...urls].slice(0, 2) });
    } catch (_) { toast.error("Immagine non valida"); }
  };

  return (
    <div className="space-y-3.5">
      <div className="space-y-1.5">
        <Label>Titolo</Label>
        <Input value={draft.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Es. Sconto benvenuto" className="h-11 rounded-xl" data-testid={`${idPrefix}-title`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Tipo sconto</Label>
          <Select value={draft.discount_type || "percent"} onValueChange={(v) => onChange({ discount_type: v })}>
            <SelectTrigger className="h-11 rounded-xl" data-testid={`${idPrefix}-dtype`}><SelectValue /></SelectTrigger>
            <SelectContent>{DTYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{draft.discount_type === "percent" ? "Percentuale" : draft.discount_type === "bogo" ? "—" : "Valore €"}</Label>
          <Input type="number" min="0" value={draft.discount_value ?? 0} disabled={valDisabled}
            onChange={(e) => onChange({ discount_value: e.target.value })} className="h-11 rounded-xl" data-testid={`${idPrefix}-dvalue`} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Spesa minima € (opzionale)</Label>
        <Input type="number" min="0" value={draft.min_spend ?? 0} onChange={(e) => onChange({ min_spend: e.target.value })} className="h-11 rounded-xl" data-testid={`${idPrefix}-minspend`} />
      </div>
      <div className="space-y-1.5">
        <Label>Descrizione</Label>
        <Textarea value={draft.description || ""} onChange={(e) => onChange({ description: e.target.value })} rows={3} maxLength={600}
          placeholder="Dettagli e condizioni del coupon" className="rounded-xl" data-testid={`${idPrefix}-desc`} />
      </div>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1"><ImagePlus className="h-4 w-4" /> Immagini (max 2)</Label>
        <div className="flex items-center gap-2" data-testid={`${idPrefix}-images`}>
          {(draft.images || []).map((img, k) => (
            <div key={k} className="relative h-20 w-20 rounded-xl overflow-hidden border border-slate-200 shrink-0">
              <img src={img} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => onChange({ images: (draft.images || []).filter((_, idx) => idx !== k) })}
                data-testid={`${idPrefix}-img-remove-${k}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {(draft.images || []).length < 2 && (
            <label className="h-20 w-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 cursor-pointer hover:border-[#E63946] hover:text-[#E63946] shrink-0" data-testid={`${idPrefix}-img-add`}>
              <ImagePlus className="h-6 w-6" />
              <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} />
            </label>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 p-3">
        <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
          <Switch checked={!!draft.has_expiry} onCheckedChange={(v) => onChange({ has_expiry: v })} data-testid={`${idPrefix}-expiry-toggle`} /> Con scadenza
        </label>
        {draft.has_expiry && (
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            valido <Input type="number" min="1" value={draft.expiry_days ?? 30} onChange={(e) => onChange({ expiry_days: e.target.value })} className="h-9 w-16 rounded-lg" data-testid={`${idPrefix}-days`} /> giorni
          </div>
        )}
      </div>
    </div>
  );
}
